import "@aws-amplify/ui-react/styles.css";

import { Auth, Hub } from "aws-amplify";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { ThemeProvider } from "@aws-amplify/ui-react";

import { theme } from "./Components/Authenticator/theme";
import { clearAll, setToken } from "./Store/authSlice";
import { clearSavedInviteParams, clearSavedUrlParams } from "./utils/urlParams";
import LoginPage from "./Pages/LoginPage";
import { RootState } from "./Store/store";

type AuthenticatedUserProps = {
  children: ReactNode;
};

const IDLE_EVENTS: (keyof WindowEventMap)[] = ["mousemove", "keydown", "click", "scroll", "touchstart"];
const WARNING_AFTER_MS = 14 * 60 * 1000; // show warning after 14 min idle
const COUNTDOWN_SECS = 60;               // 60-second countdown before forced logout

// When the user opted in at login, keep the session alive ~24h so long uploads
// don't trip the idle logout. Total = idle window + countdown ≈ 24h.
const EXTENDED_WARNING_AFTER_MS = 24 * 60 * 60 * 1000 - COUNTDOWN_SECS * 1000;
const getWarningAfterMs = () =>
  localStorage.getItem("stayLoggedIn24h") === "true" ? EXTENDED_WARNING_AFTER_MS : WARNING_AFTER_MS;

function AuthenticatedUser(props: AuthenticatedUserProps) {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(COUNTDOWN_SECS);

  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownInterval = useRef<ReturnType<typeof setInterval> | null>(null);
  // Stable ref so closures inside setTimeout always call the latest resetIdleTimer
  const resetIdleTimerRef = useRef<() => void>(() => {});

  const stopCountdown = () => {
    if (countdownInterval.current) {
      clearInterval(countdownInterval.current);
      countdownInterval.current = null;
    }
  };

  const doSignOut = useCallback(async () => {
    stopCountdown();
    if (idleTimer.current) clearTimeout(idleTimer.current);
    setShowWarning(false);
    await Auth.signOut();
  }, []);

  const resetIdleTimer = useCallback(() => {
    if (idleTimer.current) clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => {
      // Pause activity listeners while the warning modal is visible
      IDLE_EVENTS.forEach((e) => window.removeEventListener(e, resetIdleTimerRef.current));
      setShowWarning(true);
      setCountdown(COUNTDOWN_SECS);
      countdownInterval.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            void doSignOut();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, getWarningAfterMs());
  }, [doSignOut]);

  resetIdleTimerRef.current = resetIdleTimer;

  const stayLoggedIn = useCallback(() => {
    stopCountdown();
    setShowWarning(false);
    setCountdown(COUNTDOWN_SECS);
    // Restore activity listeners and restart the idle timer
    IDLE_EVENTS.forEach((e) => window.addEventListener(e, resetIdleTimerRef.current, { passive: true }));
    resetIdleTimerRef.current();
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    IDLE_EVENTS.forEach((e) => window.addEventListener(e, resetIdleTimer, { passive: true }));
    resetIdleTimer();
    return () => {
      IDLE_EVENTS.forEach((e) => window.removeEventListener(e, resetIdleTimer));
      if (idleTimer.current) clearTimeout(idleTimer.current);
      stopCountdown();
    };
  }, [isAuthenticated, resetIdleTimer]);

  useEffect(() => {
    Auth.currentAuthenticatedUser().then(
      (authUser) => {
        dispatch(setToken(authUser.attributes["custom:tenantName"]));
        setIsCheckingAuth(false);
      },
      (reason) => {
        setIsCheckingAuth(false);
      },
    );
    Hub.listen("auth", ({ payload }) => {
      switch (payload.event) {
        case "signIn": {
          dispatch(setToken(payload.data.attributes["custom:tenantName"]));
          break;
        }
        case "signOut": {
          try {
            // Clear all localStorage keys used by the app
            clearSavedUrlParams();
            clearSavedInviteParams();
            localStorage.clear();
          } catch (e) {}
          dispatch(clearAll());
          break;
        }
      }
    });
  }, [dispatch]);

  // Show loading state while checking authentication
  if (isCheckingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-base-100">
        <div className="loading loading-spinner loading-lg text-primary"></div>
      </div>
    );
  }

  // Show custom login page if not authenticated
  if (!isAuthenticated) {
    return <LoginPage />;
  }

  // Show the main app if authenticated
  return (
    <ThemeProvider theme={theme}>
      {props.children}

      {showWarning && (
        <>
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[999]" aria-hidden="true" />
          <div
            className="fixed inset-0 z-[1000] flex items-center justify-center p-6"
            role="dialog"
            aria-modal="true"
            aria-labelledby="session-warning-title"
          >
            <div className="modal-box max-w-sm w-full p-6 flex flex-col gap-4">
              <h3 id="session-warning-title" className="text-lg font-bold">
                Session expiring soon
              </h3>
              <p className="text-sm text-base-content/70">
                You've been inactive. Do you want to stay logged in?
              </p>
              <p className="text-3xl font-mono font-bold text-center text-warning">
                {countdown}s
              </p>
              <div className="flex gap-3 justify-end">
                <button className="btn btn-ghost btn-sm" onClick={() => void doSignOut()}>
                  Log out
                </button>
                <button className="btn btn-primary btn-sm" onClick={stayLoggedIn}>
                  Stay logged in
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </ThemeProvider>
  );
}

export default AuthenticatedUser;
